import React, { ChangeEvent, ReactElement, useState } from 'react';
import {
  Button,
  InsetText,
  LoaderButton,
  Table,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from 'idsk-react-components';
import { BackButton, ConfirmModal } from 'slovlex-idsk-react-components';
import './podpisPredseda.scss';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import SprievodnyDokumentMergedItem from '../../../model/pracovnyKrok/SprievodnyDokumentMergedItem';
import Actions from '../../SprievodnyDokument/SprievodnyDokumentUpdateList/component/Actions';
import downloadFile from '../../../function/downloadFile';
import { useClient, useClientStatusContext } from '../../../context/ClientStatusContext';
import { savePodpisPredseduClient } from '../../../client/pracovnyKrok/podpisPredseduClient';
import routes from '../../../routing/routes';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { usePodpisPredsedaFormDataContext } from '../../../context/PodpisPredsedaFormDataContext';
import getFileSize from '../../../function/getFileSize';

const PodpisPredseda = (): ReactElement => {
  const { obalMaterialuFormData, generatedFile } = usePodpisPredsedaFormDataContext();
  const {
    type,
    year,
    index,
    pracovnyKrokId,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, pracovnyKrokId: string }>();
  const typObalMaterialu = {
    uuid: 'uuid',
    nazov: 'Obal materiálu',
    kod: 'ObalMaterialuBezchybnost',
    povolitZadavanieFormularom: true,
    existujeFormular: true,
    platny: true,
    generovanySystemom: false,
    legislativny: false,
  };
  const [dokumentToBeUploaded, setDokumentToBeUploaded] = useState<SprievodnyDokumentMergedItem>({
    typDokumentu: typObalMaterialu,
    formData: obalMaterialuFormData && 'there is formData',
    sprievodnyDokumentUuid: generatedFile && '00000000-00000000-00000000-00000000',
    velkost: generatedFile?.size,
    povinny: true,
    nazov: generatedFile?.name,
  });
  const [fileToBeUploaded, setFileToBeUploaded] = useState<File>(generatedFile);
  const navigate = useNavigate();
  const {
    handleStatus,
    clearStatus,
  } = useClientStatusContext();
  const { displaySuccess } = useSuccessMessageContext();
  const uploadDokument = (e: ChangeEvent<HTMLInputElement>): void => {
    clearStatus();
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileToBeUploaded(file);
      setDokumentToBeUploaded({
        ...dokumentToBeUploaded,
        sprievodnyDokumentUuid: '00000000-00000000-00000000-00000000',
        nazov: file.name,
        velkost: file.size,
      });
      displaySuccess('Dokument bol úspešne pridaný.');
    }
  };

  const deleteDokument = (): void => {
    setFileToBeUploaded(undefined);
    setDokumentToBeUploaded({ typDokumentu: typObalMaterialu });
    displaySuccess('Dokument bol úspešne odstránený.');
  };

  const downloadDokument = (): void => {
    downloadFile(dokumentToBeUploaded?.nazov, fileToBeUploaded);
  };

  const {
    wrapClient: savePodpisPredsedu,
    status: podpisPredsedaStatus,
  } = useClient(savePodpisPredseduClient);
  const uploadPodpisPredsedu = async (): Promise<void> => {
    clearStatus();
    if (fileToBeUploaded) {
      await savePodpisPredsedu(type, year, index, pracovnyKrokId, fileToBeUploaded);
      navigate(routes.procesy.detail.withParams(type, year, index));
      displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
    } else {
      showError();
    }
  };
  const showError = (): void => {
    const err = {
      generalErrors: [{
        message: 'Akciu nie je možné vykonať. '
          + 'Prosím, pridajte na aktuálnej obrazovke obal materiálu NR SR za bezchybnosť.',
      }],
      fieldErrors: [],
      loading: undefined,
      finished: undefined,
      responseCode: undefined,
      failed: undefined,
    };
    handleStatus(err);
  };

  return (
    <div>
      <Table>
        <Thead>
          <Tr>
            <Th width="35%">
              Názov
            </Th>
            <Th width="20%">
              Typ dokumentu
            </Th>
            <Th width="10%">Veľkosť</Th>
            <Th width="35%" />
          </Tr>
        </Thead>
        <Tbody>
          <Tr>
            <Td>{dokumentToBeUploaded?.nazov}</Td>
            <Td>
              <Tag size="medium">
                Povinné
              </Tag><br />
              {typObalMaterialu.nazov}
            </Td>
            <Td>
              {dokumentToBeUploaded?.velkost !== undefined
                && dokumentToBeUploaded?.velkost !== null
                && getFileSize(dokumentToBeUploaded.velkost)}
            </Td>
            <Td>
              <Actions
                sprievodnyDokument={dokumentToBeUploaded}
                uploadDokument={uploadDokument}
                uploadDokumentStatus={undefined}
                deleteDokument={deleteDokument}
                downloadDokument={downloadDokument}
              />
            </Td>
          </Tr>
        </Tbody>
      </Table>
      <InsetText type="warn">
        Tento dokument bude spolu so všetkými pridanými sprievodnými dokumentami k aktuálnemu štádiu
        “Spracovanie
        predkladateľom” súčasťou odoslanej notifikácie a nebude pridaný medzi sprievodné dokumenty
        materiálu.
      </InsetText>
      <div className="podpis-predseda-actions">
        <ConfirmModal
          question="Naozaj si prajete  odoslať emailovú notifikáciu predsedovi vlády?"
          description={'Po vykonaní tohto pracovného kroku bude odoslaná emailová notifikácia '
          + 'predsedovi vlády s vytvoreným obalom materiálu NR SR za bezchybnosť a všetkými '
          + 'aktuálne pridanými sprievodnými dokumentami k štádiu "Spracovanie predkladateľ".'}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              className="podpis-predseda-actions__odoslat-notifikaciu"
              size="large"
              type="button"
              loading={podpisPredsedaStatus?.loading}
              disableOutsideClickWhileLoading
            >Odoslať notifikáciu so zadaným obalom
            </LoaderButton>
          )}
          doAfterConfirm={uploadPodpisPredsedu}
        />
        <BackButton disabled={podpisPredsedaStatus?.loading} />
      </div>
    </div>
  );
};

export default PodpisPredseda;
